import {
  WeightAlertRecord,
  WeightAlertRecordExport,
} from '../Interfaces/Alerts/AlertRecord';
import { ApiEnums } from '../Interfaces/ApiEnums';
import {
  NodeInfo,
  AssetInfo,
  GridRowAssetTableInfo,
} from '../Interfaces/Assets/AssetInfo';
import { NodeConfigInfo } from '../Interfaces/Config/NodeConfigInfo';
import { ConfigSetting } from '../Interfaces/Config/ConfigSetting';
import {
  useChipStylesBlue,
  useChipStylesGreen,
  useChipStylesGrey,
  useChipStylesOrange,
  useChipStylesRed,
  useChipStylesDarkOrange,
} from '../components/ChipStyles';
import {
  arrowRotateCcwIcon,
  cancelIcon,
  checkCircleIcon,
  clockArrowRotateIcon,
  exclamationCircleIcon,
  plusCircleIcon,
  questionCircleIcon,
  xCircleIcon,
} from '@progress/kendo-svg-icons';
import { registrationStatusesNameMap } from './textMappings';

export function dateFormat(locale: string) {
  let options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };
  let today = new Date();

  return today.toLocaleDateString(locale, options);
}

export function formatDateToLocaleDateString(
  locale: string,
  date: Date | undefined,
) {
  let options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  return date?.toLocaleDateString(locale, options);
}

export function formatDateForExport(locale: string) {
  let options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  };

  return new Intl.DateTimeFormat(locale, options).format(new Date());
}

export const getCurrentDateYYYYMMDD = () => {
  let date: Date = new Date();
  return `${date.getFullYear()}${date.getMonth() + 1}${date.getDate()}`;
};

export const getDateWithoutTimezone = (date: Date) => {
  var _date = new Date(date);
  const tzoffset = _date.getTimezoneOffset() * 60000; //offset in milliseconds
  const withoutTimezone = new Date(_date.valueOf() - tzoffset)
    .toISOString()
    .slice(0, -1);
  return withoutTimezone;
};

export const getLocalDateTime = (utcDate: Date): string => {
  let date: string = utcDate.toString();
  if (!date.endsWith('Z')) date += 'Z';
  return new Date(date).toLocaleString();
};

export const getDateTimeLocaleString = (localDate: Date): string => {
  let date: string = localDate.toString();
  if (date == '0001-01-01T00:00:00') {
    return '';
  }
  return new Date(date).toLocaleString();
};

export const getLocaleString = (date: Date): string => {
  return new Date(date).toLocaleString();
};

export const getRequestOptions = (token: string | undefined) => {
  return {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token !== undefined ? `Bearer ${token}` : '',
    },
  };
};

export const postRequestOptions = (token: string | undefined, data: any) => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token !== undefined ? `Bearer ${token}` : '',
    },
    body: JSON.stringify(data),
  };
};

export const putRequestOptions = (token: string | undefined, data?: any) => {
  return {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token !== undefined ? `Bearer ${token}` : '',
    },
    ...(data && { body: JSON.stringify(data) }),
  };
};

export const postFormRequestOptions = (
  token: string | undefined,
  data?: any,
) => {
  return {
    method: 'POST',
    headers: {
      Authorization: token !== undefined ? `Bearer ${token}` : '',
    },
    body: data,
  };
};

export const fetchJsonData = async (url: string, token: string | undefined) => {
  const response = await fetch(url, getRequestOptions(token)).catch(error => {
    console.error(error);
    return Promise.reject(error);
  });

  if (!response.ok) return Promise.reject(response.statusText);

  return await response.json();
};

export const postJsonData = async (
  url: string,
  token: string | undefined,
  data: any,
) => {
  const response = await fetch(url, postRequestOptions(token, data)).catch(
    error => {
      console.error(error);
      return Promise.reject(error);
    },
  );

  if (!response.ok) return Promise.reject(response.statusText);

  return await response.json();
};

export const convertAlertGridDataToCsvData = (
  alert: WeightAlertRecord,
): WeightAlertRecordExport => {
  return {
    AlertID: alert.AlertID,
    ItemDesc: alert.ItemDesc,
    Barcode: alert.Barcode,
    CreatedOn: getLocalDateTime(alert.CreatedOn),
    Division: alert.Division,
    SiteNumber: alert.SiteNumber,
    NodeGroup: alert.NodeGroup,
    LaneNumber: alert.LaneNumber,
    MachineName: alert.MachineName,
    NodeID: alert.NodeID,
    AlertType: ApiEnums.AlertType[alert.AlertType],
    Count: alert.Count,
    OperatorID: alert.OperatorID,
    CommandStatus: ApiEnums.CommandStatus[alert.CommandStatus],
    RecommendedCommand: ApiEnums.AlertCommand[alert.RecommendedCommand],
    SelectedCommand: ApiEnums.AlertCommand[alert.SelectedCommand],
    WeightRecord: alert.WeightRecord,
    ReferenceWeight: alert.ReferenceWeight,
    WeightDelta: alert.WeightDelta,
    WeightCategory: ApiEnums.WeightCategory[alert.WeightCategory],
    Threshold: alert.Threshold,
    FixedWeight: alert.FixedWeight,
    Timestamp: formatTimestampDate(alert.Timestamp),
  } as WeightAlertRecordExport;
};

export const flattenAssetData = (
  nodeAssets: NodeInfo[] | NodeInfo,
): GridRowAssetTableInfo[] => {
  let result: GridRowAssetTableInfo[] = [];

  if (Array.isArray(nodeAssets)) {
    nodeAssets.forEach(nodeAsset =>
      nodeAsset.AssetInfoList.map(AssetInfo =>
        result.push(getGridRowAssetInfo(nodeAsset, AssetInfo)),
      ),
    );
  } else {
    nodeAssets.AssetInfoList.map(AssetInfo =>
      result.push(getGridRowAssetInfo(nodeAssets, AssetInfo)),
    );
  }

  return result;
};

export const flattenNodeConfigData = (
  nodeAssets: NodeConfigInfo[] | NodeConfigInfo,
): GridRowAssetTableInfo[] => {
  let result: GridRowAssetTableInfo[] = [];

  if (Array.isArray(nodeAssets)) {
    nodeAssets.forEach(nodeAsset =>
      nodeAsset.ConfigSettingList.map(ConfigInfo =>
        result.push(getGridRowNodeConfigInfo(nodeAsset, ConfigInfo)),
      ),
    );
  } else {
    nodeAssets.ConfigSettingList.map(ConfigInfo =>
      result.push(getGridRowNodeConfigInfo(nodeAssets, ConfigInfo)),
    );
  }

  return result;
};

export const getGridRowNodeConfigInfo = (
  nodeAsset: NodeConfigInfo,
  settingInfo: ConfigSetting,
): GridRowAssetTableInfo => {
  let result: GridRowAssetTableInfo = {
    NodeId: nodeAsset.NodeInfo.NodeID,
    SiteNumber: nodeAsset.NodeInfo.SiteNumber,
    Division: nodeAsset.NodeInfo.Division,
    NodeType: nodeAsset.NodeInfo.NodeType,
    NodeGroup: nodeAsset.NodeInfo.NodeGroup,
    LaneNumber: nodeAsset.NodeInfo.LaneNumber,
    LastHeartbeat: getLocalDateTime(nodeAsset.NodeInfo.LastHeartbeat),
    Category: settingInfo.Category,
    Name: settingInfo.Name,
    Value: settingInfo.Value,
  };

  return result;
};

export const getGridRowAssetInfo = (
  nodeAsset: NodeInfo,
  assetInfo: AssetInfo,
): GridRowAssetTableInfo => {
  let result: GridRowAssetTableInfo = {
    NodeId: nodeAsset.NodeID,
    MachineName: nodeAsset.MachineName,
    MacAddress: nodeAsset.MacAddress,
    SiteNumber: nodeAsset.SiteNumber,
    Division: nodeAsset.Division,
    LineOfBusiness: nodeAsset.LineOfBusiness,
    NodeType: nodeAsset.NodeType,
    NodeGroup: nodeAsset.NodeGroup,
    LaneNumber: nodeAsset.LaneNumber,
    CreationDate: getLocalDateTime(nodeAsset.CreationDate),
    LastHeartbeat: getLocalDateTime(nodeAsset.LastHeartbeat),
    Category: assetInfo.Category,
    Name: assetInfo.Name,
    Value: assetInfo.Value,
  };

  return result;
};

export const getDaysDiff = (lastHeartbeat: string) => {
  const msPerDay = 86400000; // 60 * 1000 * 60 * 24;

  return Math.abs(Date.now() - new Date(lastHeartbeat).getTime()) / msPerDay;
};

export const formatTimestampDate = (timestamp: Date): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  let hours = date.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const paddedHours = String(hours).padStart(2, '0');

  return `${month}/${day}/${year}, ${paddedHours}:${minutes}:${seconds} ${ampm}`;
};

export const fomatDateToLocaleISODate = (date: Date | null | undefined) => {
  if (!date) return;

  const { year, month, day, hours, minutes, seconds, milliseconds } = {
    year: date.getFullYear(),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    day: String(date.getDate()).padStart(2, '0'),
    hours: String(date.getHours()).padStart(2, '0'),
    minutes: String(date.getMinutes()).padStart(2, '0'),
    seconds: String(date.getSeconds()).padStart(2, '0'),
    milliseconds: String(date.getMilliseconds()).padStart(3, '0'),
  };

  const isoDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;

  return isoDateString.split('T')[0];
};

export const convertStringTodate = (date: any) => {
  let parts = date?.split('-');
  let formattedDate = new Date(parts);
  return formattedDate;
};

export const isNullOrEmptyString = (data: string) => {
  return data == null || data == '';
};

export const getDate = (dateData: Date) => {
  return dateData.toString().split('T')[0];
};

export const dateSubtract = (days: number, isStartDate: boolean) => {
  let date = new Date();

  date.setDate(date.getDate() - days);

  if (isStartDate) date.setHours(0, 0, 0, 0);

  return date;
};

export const dateAdd = (days: number, midnight: boolean) => {
  let date = new Date();

  date.setDate(date.getDate() + days);

  if (midnight) date.setHours(0, 0, 0, 0);

  return date;
};

export const getDateRange = (
  range: ApiEnums.DateRangeOptions,
): [Date, Date] | undefined => {
  switch (range) {
    case ApiEnums.DateRangeOptions.Today:
      return [dateSubtract(0, true), dateAdd(1, true)];
    case ApiEnums.DateRangeOptions.Yesterday:
      return [dateSubtract(1, true), dateSubtract(0, true)];
    case ApiEnums.DateRangeOptions.LastSevenDays:
      return [dateSubtract(7, true), dateAdd(1, true)];
    case ApiEnums.DateRangeOptions.LastThirtyDays:
      return [dateSubtract(30, true), dateAdd(1, true)];
    case ApiEnums.DateRangeOptions.Other:
      break;
    default:
      break;
  }
};

export const getStartDateTimeLocaleString = (localDate: Date): string => {
  let date: string = localDate.toString();
  const newDate: Date = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate.toLocaleString();
};

export const getEndDateTimeLocaleString = (localDate: Date): string => {
  let date: string = localDate.toString();
  const newDate: Date = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate.toLocaleString();
};

export const convertDateYYYYMMDD = (date: any) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export const maskString = (path: string) => {
  return path.replace(/\/\/([^:@]+(?::[^:@]+)?@)?/, (_, credentials) => {
    return credentials ? '//****:****@' : '//';
  });
};

export const useChipStyle = (status: ApiEnums.CommandStatus) => {
  switch (status) {
    case ApiEnums.CommandStatus.New:
      return useChipStylesBlue;
    case ApiEnums.CommandStatus.InProgress:
      return useChipStylesOrange;
    case ApiEnums.CommandStatus.Pending:
      return useChipStylesDarkOrange;
    case ApiEnums.CommandStatus.Completed:
    case ApiEnums.CommandStatus.CompletedWithErrors:
    case ApiEnums.CommandStatus.Canceled:
      return useChipStylesGreen;
    case ApiEnums.CommandStatus.Failed:
      return useChipStylesRed;
    case ApiEnums.CommandStatus.None:
      return useChipStylesGrey;
    default:
      return useChipStylesGrey;
  }
};

export const commandStatusIconMap = (status: ApiEnums.CommandStatus) => {
  switch (status) {
    case ApiEnums.CommandStatus.New:
      return plusCircleIcon;
    case ApiEnums.CommandStatus.InProgress:
      return arrowRotateCcwIcon;
    case ApiEnums.CommandStatus.Completed:
      return checkCircleIcon;
    case ApiEnums.CommandStatus.CompletedWithErrors:
      return exclamationCircleIcon;
    case ApiEnums.CommandStatus.Canceled:
      return cancelIcon;
    case ApiEnums.CommandStatus.Failed:
      return xCircleIcon;
    case ApiEnums.CommandStatus.None:
      return questionCircleIcon;
    case ApiEnums.CommandStatus.Pending:
      return clockArrowRotateIcon;
    default:
      return questionCircleIcon;
  }
};

export const useChipStyleScheduledCommand = (
  status: ApiEnums.ScheduledCommandStatus,
) => {
  switch (status) {
    case ApiEnums.ScheduledCommandStatus.ExecutionInProgress:
      return useChipStylesOrange;
    case ApiEnums.ScheduledCommandStatus.Pending:
      return useChipStylesDarkOrange;
    case ApiEnums.ScheduledCommandStatus.Completed:
    case ApiEnums.ScheduledCommandStatus.CompletedWithErrors:
    case ApiEnums.ScheduledCommandStatus.ExecutionCompletedWithErrors:
    case ApiEnums.ScheduledCommandStatus.ExecutionCompleted:
    case ApiEnums.ScheduledCommandStatus.Canceled:
      return useChipStylesGreen;
    case ApiEnums.ScheduledCommandStatus.ExecutionFailed:
    case ApiEnums.ScheduledCommandStatus.Failed:
      return useChipStylesRed;
    case ApiEnums.ScheduledCommandStatus.None:
      return useChipStylesGrey;
    default:
      return useChipStylesGrey;
  }
};

export const scheduledCommandStatusIconMap = (
  status: ApiEnums.ScheduledCommandStatus,
) => {
  switch (status) {
    case ApiEnums.ScheduledCommandStatus.ExecutionInProgress:
      return arrowRotateCcwIcon;
    case ApiEnums.ScheduledCommandStatus.ExecutionCompleted:
    case ApiEnums.ScheduledCommandStatus.Completed:
      return checkCircleIcon;
    case ApiEnums.ScheduledCommandStatus.ExecutionCompletedWithErrors:
    case ApiEnums.ScheduledCommandStatus.CompletedWithErrors:
      return exclamationCircleIcon;
    case ApiEnums.ScheduledCommandStatus.Canceled:
      return cancelIcon;
    case ApiEnums.ScheduledCommandStatus.ExecutionFailed:
    case ApiEnums.ScheduledCommandStatus.Failed:
      return xCircleIcon;
    case ApiEnums.ScheduledCommandStatus.None:
      return questionCircleIcon;
    case ApiEnums.ScheduledCommandStatus.Pending:
      return clockArrowRotateIcon;
    default:
      return questionCircleIcon;
  }
};

export const getCommandStatusToEnum = (status: string) => {
  switch (status) {
    case 'New':
    case ApiEnums.CommandStatus.New.toString():
      return ApiEnums.CommandStatus.New;
    case 'InProgress':
    case ApiEnums.CommandStatus.InProgress.toString():
      return ApiEnums.CommandStatus.InProgress;
    case 'Pending':
    case ApiEnums.CommandStatus.Pending.toString():
      return ApiEnums.CommandStatus.Pending;
    case 'Completed':
    case ApiEnums.CommandStatus.Completed.toString():
      return ApiEnums.CommandStatus.Completed;
    case 'CompletedWithErrors':
    case ApiEnums.CommandStatus.CompletedWithErrors.toString():
      return ApiEnums.CommandStatus.CompletedWithErrors;
    case 'Canceled':
    case ApiEnums.CommandStatus.Canceled.toString():
      return ApiEnums.CommandStatus.Canceled;
    case 'Failed':
    case ApiEnums.CommandStatus.Failed.toString():
      return ApiEnums.CommandStatus.Failed;
    case 'None':
    case ApiEnums.CommandStatus.None.toString():
      return ApiEnums.CommandStatus.None;
    default:
      return ApiEnums.CommandStatus.None;
  }
};

export const getScheduledCommandStatusToEnum = (status: string) => {
  switch (status) {
    case 'Pending':
    case ApiEnums.ScheduledCommandStatus.Pending.toString():
      return ApiEnums.ScheduledCommandStatus.Pending;
    case 'ExecutionInProgress':
    case ApiEnums.ScheduledCommandStatus.ExecutionInProgress.toString():
      return ApiEnums.ScheduledCommandStatus.ExecutionInProgress;
    case 'ExecutionCompleted':
    case ApiEnums.ScheduledCommandStatus.ExecutionCompleted.toString():
      return ApiEnums.ScheduledCommandStatus.ExecutionCompleted;
    case 'ExecutionCompletedWithErrors':
    case ApiEnums.ScheduledCommandStatus.ExecutionCompletedWithErrors.toString():
      return ApiEnums.ScheduledCommandStatus.ExecutionCompletedWithErrors;
    case 'ExecutionFailed':
    case ApiEnums.ScheduledCommandStatus.ExecutionFailed.toString():
      return ApiEnums.ScheduledCommandStatus.ExecutionFailed;
    case 'Completed':
    case ApiEnums.ScheduledCommandStatus.Completed.toString():
      return ApiEnums.ScheduledCommandStatus.Completed;
    case 'CompletedWithErrors':
    case ApiEnums.ScheduledCommandStatus.CompletedWithErrors.toString():
      return ApiEnums.ScheduledCommandStatus.CompletedWithErrors;
    case 'Failed':
    case ApiEnums.ScheduledCommandStatus.Failed.toString():
      return ApiEnums.ScheduledCommandStatus.Failed;
    case 'Canceled':
    case ApiEnums.ScheduledCommandStatus.Canceled.toString():
      return ApiEnums.ScheduledCommandStatus.Canceled;
    case 'None':
    case ApiEnums.ScheduledCommandStatus.None.toString():
      return ApiEnums.ScheduledCommandStatus.None;
    default:
      return ApiEnums.ScheduledCommandStatus.None;
  }
};

export const exportAssetTableData = (
  nodeAsset: NodeInfo[],
  assetInfo?: AssetInfo[],
) => {
  const result: GridRowAssetTableInfo[] = [];
  nodeAsset.forEach(nodeData => {
    const data: GridRowAssetTableInfo = {
      Division: nodeData.Division,
      SiteNumber: nodeData.SiteNumber,
      NodeGroup: nodeData.NodeGroup,
      LaneNumber: nodeData.LaneNumber,
      NodeType: nodeData.NodeType,
    };

    assetInfo?.forEach(
      assetProp =>
        (data[assetProp.Category + assetProp.Name] =
          nodeData.AssetInfoList.find(
            (asset: AssetInfo) =>
              asset.Category == assetProp.Category &&
              asset.Name == assetProp.Name,
          )?.Value),
    );

    data.RegistrationStatus = registrationStatusesNameMap(
      nodeData.ManagementAction,
    );
    data.LastHeartbeat = getLocalDateTime(nodeData.LastHeartbeat);
    delete data.LineOfBusiness;

    result.push(data);
  });

  return result;
};

export function convertToLocaleDateString(date: Date) {
  return new Date(date).toLocaleDateString();
}

export function convertToLocaleTimeString(date: Date) {
  return new Date(date).toLocaleTimeString();
}

export const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

export const isBBoxRectObject = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;

  const v = value as Record<string, unknown>;

  const hasX = isFiniteNumber(v.x);
  const hasY = isFiniteNumber(v.y);
  const hasWH =
    (isFiniteNumber(v.w) && isFiniteNumber(v.h)) ||
    (isFiniteNumber(v.width) && isFiniteNumber(v.height));

  return hasX && hasY && hasWH;
};

export const isBBoxRectArray = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(item => isBBoxRectObject(item));
